import React, { useState, useEffect } from 'react';
import { X, Tag, Check, Loader, Plus, Trash2 } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { createShiftType, updateShiftType } from '../../../services/api/messageCenterShiftTypes';
import TimePicker from '../../../components/ui/TimePicker/TimePicker';
import FieldError from './FieldError';
import { validateRanges, blockInvalidNumberKeys, blockNonIntegerKeys, sanitizeNumber } from '../utils/rangeValidation';
import styles from './ShiftTypeModal.module.css';

const EMPTY_SLAB = { minValue: '', maxValue: '', rate: '' };

const EMPTY_FORM = {
  name: '',
  defaultStartTime: '',
  defaultEndTime: '',
  defaultTarget: '',
  isActive: true,
  slabs: [{ ...EMPTY_SLAB }],
};

const ShiftTypeModal = ({ mode, shiftType, onClose, onSuccess }) => {
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (mode === 'edit' && shiftType) {
      setFormData({
        name: shiftType.name || '',
        defaultStartTime: shiftType.defaultStartTime || '',
        defaultEndTime: shiftType.defaultEndTime || '',
        defaultTarget: shiftType.defaultTarget != null ? String(shiftType.defaultTarget) : '',
        isActive: shiftType.isActive ?? true,
        slabs: Array.isArray(shiftType.commissionSlabs) && shiftType.commissionSlabs.length > 0
          ? shiftType.commissionSlabs.map((s) => ({
              minValue: s.minValue != null ? String(s.minValue) : '',
              maxValue: s.maxValue != null ? String(s.maxValue) : '',
              rate: s.rate != null ? String(s.rate) : '',
            }))
          : [{ ...EMPTY_SLAB }],
      });
    } else {
      setFormData(EMPTY_FORM);
    }
  }, [mode, shiftType]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleAddSlab = () => {
    setFormData((prev) => {
      const last = prev.slabs[prev.slabs.length - 1];
      const nextMin = last && /^\d+$/.test(last.maxValue) ? String(parseInt(last.maxValue, 10) + 1) : '';
      return { ...prev, slabs: [...prev.slabs, { ...EMPTY_SLAB, minValue: nextMin }] };
    });
    setErrors({});
  };

  const handleSlabChange = (index, field, value) => {
    const clean = sanitizeNumber(value, field === 'rate');
    setFormData((prev) => {
      const slabs = [...prev.slabs];
      slabs[index] = { ...slabs[index], [field]: clean };
      return { ...prev, slabs };
    });
    const errorKey = `slabs.${index}.${field}`;
    const rowKey = `slabs.${index}.row`;
    if (errors[errorKey] || errors[rowKey]) {
      setErrors((prev) => ({ ...prev, [errorKey]: '', [rowKey]: '' }));
    }
  };

  const handleRemoveSlab = (index) => {
    setFormData((prev) => ({ ...prev, slabs: prev.slabs.filter((_, i) => i !== index) }));
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = t('messageCenterShiftTypes.validation.nameRequired');
    }
    if (!formData.defaultStartTime) {
      newErrors.defaultStartTime = t('messageCenterShiftTypes.validation.startTimeRequired');
    }
    if (!formData.defaultEndTime) {
      newErrors.defaultEndTime = t('messageCenterShiftTypes.validation.endTimeRequired');
    }

    Object.assign(newErrors, validateRanges(formData.slabs, { valueField: 'rate', prefix: 'slabs', t, maxValue: 100 }));

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showError(t('messageCenterShiftTypes.validation.fixErrors'));
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        defaultStartTime: formData.defaultStartTime,
        defaultEndTime: formData.defaultEndTime,
        defaultTarget: formData.defaultTarget === '' ? null : parseFloat(formData.defaultTarget),
        isActive: formData.isActive,
        commissionMethod: 'per_sales_value',
        commissionSlabs: formData.slabs.map((s) => ({
          minValue: parseFloat(s.minValue),
          maxValue: s.maxValue === '' ? null : parseFloat(s.maxValue),
          rate: parseFloat(s.rate),
        })),
      };

      if (mode === 'edit') {
        await updateShiftType(shiftType.id, payload);
        success(t('messageCenterShiftTypes.shiftTypeUpdatedSuccess'));
      } else {
        await createShiftType(payload);
        success(t('messageCenterShiftTypes.shiftTypeCreatedSuccess'));
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving shift type:', err);
      showError(err.response?.data?.message || t('messageCenterShiftTypes.failedToSaveShiftType'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const minPlaceholder = (index) => {
    if (index === 0) return t('messageCenterShiftTypes.placeholders.firstMin');
    const prevMax = formData.slabs[index - 1]?.maxValue;
    return /^\d+$/.test(prevMax)
      ? t('messageCenterShiftTypes.placeholders.nextMin', { value: parseInt(prevMax, 10) + 1 })
      : '';
  };

  return (
    <div className={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) onClose(); }}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{mode === 'edit' ? t('messageCenterShiftTypes.editShiftType') : t('messageCenterShiftTypes.createShiftType')}</h2>
          <button onClick={onClose} className={styles.closeButton} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalBody}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label htmlFor="name">{t('messageCenterShiftTypes.name')} *</label>
              <div className={styles.inputWrapper}>
                <Tag className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  placeholder={t('messageCenterShiftTypes.placeholders.name')}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className={errors.name ? styles.error : ''}
                  disabled={isSubmitting}
                />
              </div>
              <FieldError message={errors.name} />
            </div>

            <div className={styles.formGroup}>
              <label>{t('common.status')}</label>
              <div className={styles.toggleRow}>
                <span className={styles.toggleTitle}>{formData.isActive ? t('common.active') : t('common.inactive')}</span>
                <label className={styles.toggle}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    disabled={isSubmitting}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>{t('messageCenterShiftTypes.defaultStartTime')} *</label>
              <TimePicker
                value={formData.defaultStartTime}
                onChange={(v) => handleChange('defaultStartTime', v)}
                placeholder={t('messageCenterShiftTypes.placeholders.selectTime')}
                error={!!errors.defaultStartTime}
                disabled={isSubmitting}
              />
              <FieldError message={errors.defaultStartTime} />
            </div>

            <div className={styles.formGroup}>
              <label>{t('messageCenterShiftTypes.defaultEndTime')} *</label>
              <TimePicker
                value={formData.defaultEndTime}
                onChange={(v) => handleChange('defaultEndTime', v)}
                placeholder={t('messageCenterShiftTypes.placeholders.selectTime')}
                error={!!errors.defaultEndTime}
                disabled={isSubmitting}
              />
              <FieldError message={errors.defaultEndTime} />
            </div>

            <div className={styles.formGroup}>
              <label>{t('messageCenterShiftTypes.commissionMethod')}</label>
              <div className={styles.lockedMethod}>{t('messageCenterShiftTypes.perSalesValue')}</div>
              <p className={styles.helpText}>{t('messageCenterShiftTypes.commissionMethodHelp')}</p>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="defaultTarget">{t('messageCenterShiftTypes.defaultTarget')}</label>
              <input
                type="number"
                id="defaultTarget"
                min="0"
                step="0.01"
                className={styles.plainInput}
                value={formData.defaultTarget}
                placeholder={t('messageCenterShiftTypes.placeholders.defaultTarget')}
                onKeyDown={blockInvalidNumberKeys}
                onChange={(e) => handleChange('defaultTarget', sanitizeNumber(e.target.value))}
                disabled={isSubmitting}
              />
              <p className={styles.helpText}>{t('messageCenterShiftTypes.defaultTargetHelp')}</p>
            </div>

            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
              <div className={styles.slabsHeader}>
                <label>{t('messageCenterShiftTypes.commissionSlabs')}</label>
                <button type="button" onClick={handleAddSlab} className={styles.addSlabButton} disabled={isSubmitting}>
                  <Plus size={16} />
                  {t('messageCenterShiftTypes.addSlab')}
                </button>
              </div>

              <div className={styles.slabsList}>
                <div className={styles.slabsListHeader}>
                  <span>{t('messageCenterShiftTypes.minValue')}</span>
                  <span>{t('messageCenterShiftTypes.maxValue')}</span>
                  <span>{t('messageCenterShiftTypes.rate')}</span>
                  <span />
                </div>
                {formData.slabs.map((slab, index) => {
                  const isLast = index === formData.slabs.length - 1;
                  const err = (field) => errors[`slabs.${index}.${field}`];
                  return (
                    <div className={`${styles.slabRow} ${err('row') ? styles.rowError : ''}`} key={index}>
                      <div className={styles.fieldCell}>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          placeholder={minPlaceholder(index)}
                          value={slab.minValue}
                          onKeyDown={blockNonIntegerKeys}
                          onChange={(e) => handleSlabChange(index, 'minValue', e.target.value)}
                          className={err('minValue') ? styles.error : ''}
                          disabled={isSubmitting}
                        />
                        <FieldError message={err('minValue')} floating />
                      </div>
                      <div className={styles.fieldCell}>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          placeholder={isLast ? t('messageCenterShiftTypes.openEnded') : t('messageCenterShiftTypes.placeholders.maxValue')}
                          value={slab.maxValue}
                          onKeyDown={blockNonIntegerKeys}
                          onChange={(e) => handleSlabChange(index, 'maxValue', e.target.value)}
                          className={err('maxValue') ? styles.error : ''}
                          disabled={isSubmitting}
                        />
                        <FieldError message={err('maxValue')} floating />
                      </div>
                      <div className={styles.fieldCell}>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder={t('messageCenterShiftTypes.placeholders.rate')}
                          value={slab.rate}
                          onKeyDown={blockInvalidNumberKeys}
                          onChange={(e) => handleSlabChange(index, 'rate', e.target.value)}
                          className={err('rate') ? styles.error : ''}
                          disabled={isSubmitting}
                        />
                        <FieldError message={err('rate')} floating />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSlab(index)}
                        className={styles.removeSlabButton}
                        disabled={isSubmitting || formData.slabs.length === 1}
                        title={t('messageCenterShiftTypes.removeSlab')}
                      >
                        <Trash2 size={16} />
                      </button>                    </div>
                  );
                })}
              </div>
              <p className={styles.helpText}>{t('messageCenterShiftTypes.slabsHelp')}</p>
            </div>
          </div>
        </form>

        <div className={styles.modalFooter}>
          <button type="button" onClick={onClose} className={styles.cancelButton} disabled={isSubmitting}>
            {t('common.cancel')}
          </button>
          <button type="submit" onClick={handleSubmit} className={styles.submitButton} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader className={styles.buttonSpinner} size={18} />
                {mode === 'edit' ? t('common.updating') : t('common.creating')}
              </>
            ) : (
              <>
                <Check size={18} />
                {mode === 'edit' ? t('messageCenterShiftTypes.saveShiftType') : t('messageCenterShiftTypes.createShiftType')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShiftTypeModal;
